import { Button } from "@pepperstone/pepper-ui-kit";
import { useMemo, useState } from "react";

import type { DropdownOption } from "../../../DropDownV2";
import type { ClientTypeFilterValue } from "../../ClientTypeFilter";
import type { ClientTradingTableFilterSubProps } from "./types";

import { Datepicker } from "../../../Datepicker";
import { DropDownV2 } from "../../../DropDownV2";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { MultiSelect } from "../../../MultiSelect";
import { SearchInput } from "../../../SearchInput";
import {
  ALL_CLIENT_TYPES,
  ClientTypeFilterFields,
  includesSharedClients,
} from "../../ClientTypeFilter";
import { CLIENT_TRADING_TABLE_FILTER_REPORT_START_DATE } from "../consts";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";

export const MobileFilters = ({
  handleDateRangePicker,
  handleExportButton,
  handleSearchInput,
  handleRebateAccountsDropdown,
  handleRebateProgramsMultiSelect,
  canViewPII,
  redactedFields,
  translations: { translatedContent, language },
  onDateTooltipHover,
  showRebateProgramFilter,
  showClientTypeFilter = false,
  subIBsLoadFailed,
  onRetrySubIBs,
}: ClientTradingTableFilterSubProps) => {
  const isMobile = useIsMobile();

  const translatedLabels = useMemo(
    () => ({
      selectAccountLabel: translatedContent.dropDownLabels?.selectLabel ?? "",
      allItemsSelectedLabel:
        translatedContent.dropDownLabels?.allItemsSelectedLabel ?? "",
      itemsSelectedLabel:
        translatedContent.dropDownLabels?.itemsSelectedLabel ?? "",
      clearSelectionLabel:
        translatedContent.dropDownLabels?.clearSelectionLabel ?? "",
      selectAllLabel: translatedContent.dropDownLabels?.selectAllLabel ?? "",
      applyButtonLabel:
        translatedContent.dropDownLabels?.applyButtonLabel ?? "",
    }),
    [
      translatedContent.dropDownLabels?.allItemsSelectedLabel,
      translatedContent.dropDownLabels?.applyButtonLabel,
      translatedContent.dropDownLabels?.clearSelectionLabel,
      translatedContent.dropDownLabels?.itemsSelectedLabel,
      translatedContent.dropDownLabels?.selectAllLabel,
      translatedContent.dropDownLabels?.selectLabel,
    ],
  );

  const {
    state: {
      searchQuery,
      dateRange,
      selected: {
        rebateAccountIDs: selectedRebateAccountIDs,
        rebateProgramIDs: selectedRebateProgramIDs,
        clientTypes: selectedClientTypeValues,
        subIBIDs: selectedSubIBIDs,
      },
    },
    options: { rebateAccountIDs, rebateProgramIDs, subIBIDs },
    actions,
  } = useClientTradingReportContext();

  const selectedClientTypes =
    (selectedClientTypeValues as ClientTypeFilterValue[] | null) ??
    ALL_CLIENT_TYPES;

  const selectedSubIBs = useMemo(() => {
    return ((selectedSubIBIDs as string[]) ?? []).map((id) => ({
      label: subIBIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [subIBIDs, selectedSubIBIDs]);

  const handleClientTypesChange = (clientTypes: ClientTypeFilterValue[]) => {
    actions.setSelected("clientTypes", clientTypes);
    if (!includesSharedClients(clientTypes)) {
      actions.setSelected("subIBIDs", []);
    }
  };

  const [search, setSearch] = useState(searchQuery);

  const selectedRebateAccounts = useMemo(() => {
    return ((selectedRebateAccountIDs as string[]) ?? []).map((id) => ({
      label:
        rebateAccountIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateAccountIDs, selectedRebateAccountIDs]);

  const selectedRebatePrograms = useMemo(() => {
    return ((selectedRebateProgramIDs as string[]) ?? []).map((id) => ({
      label:
        rebateProgramIDs.find((option) => option.value === id)?.label || id,
      value: id,
    }));
  }, [rebateProgramIDs, selectedRebateProgramIDs]);

  const handleDropdownChange = (options: DropdownOption[]) => {
    handleRebateAccountsDropdown?.(options);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  const searchTitle = useMemo(() => {
    if (canViewPII) {
      if (redactedFields?.includes("name")) {
        return translatedContent?.filters?.searchWithPIIEmail;
      }
      if (redactedFields?.includes("email")) {
        return translatedContent?.filters?.searchWithPIIName;
      }
      return translatedContent?.filters?.search;
    }
    return translatedContent?.filters?.searchNoPII;
  }, [canViewPII, redactedFields, translatedContent]);

  return (
    <div data-testid="mobile-filters" className="mt-4 flex flex-col gap-4">
      <div>
        <Datepicker
          value={{
            startDate: dateRange.from,
            endDate: dateRange.to,
          }}
          inputId="dateRangeFilter"
          onChange={handleDateRangePicker}
          configs={{
            shortcuts: {
              today: translatedContent.today,
              yesterday: translatedContent.yesterday,
              past: (period) =>
                translatedContent.past ? translatedContent.past(period) : "",
              currentMonth: translatedContent.currentMonth,
              pastMonth: translatedContent.pastMonth,
            },
            footer: {
              cancel: translatedContent.cancel,
              apply: translatedContent.apply,
            },
          }}
          i18n={language}
          label={translatedContent.filters?.dates}
          allowClear={false}
          minDate={new Date(CLIENT_TRADING_TABLE_FILTER_REPORT_START_DATE)}
          tooltip={
            translatedContent.filters?.tradingReportDatePickerDisclaimerTooltip
          }
          isMobile={isMobile}
          onTooltipHover={onDateTooltipHover}
        />
      </div>
      <div>
        <DropDownV2
          id="mobileSearchDropdown"
          options={rebateAccountIDs}
          selected={selectedRebateAccounts}
          label={translatedContent.filters?.rebateAccounts}
          isMobile={isMobile}
          dropDownLabels={translatedLabels}
          handleApply={handleDropdownChange}
        />
      </div>
      {showRebateProgramFilter && (
        <div>
          <MultiSelect
            id="mobileRebateProgramFilter"
            options={rebateProgramIDs}
            selected={selectedRebatePrograms}
            label={translatedContent.filters?.rebatePrograms}
            placeholder={translatedContent.filters?.rebateProgramsPlaceholder}
            onChange={(options) => handleRebateProgramsMultiSelect?.(options)}
          />
        </div>
      )}
      {showClientTypeFilter && (
        <ClientTypeFilterFields
          context="mobile"
          subIBsLoadFailed={subIBsLoadFailed}
          onRetrySubIBs={onRetrySubIBs}
          clientTypes={selectedClientTypes}
          onClientTypesChange={handleClientTypesChange}
          subIBOptions={subIBIDs}
          selectedSubIBs={selectedSubIBs}
          onSubIBsChange={(subIBs) =>
            actions.setSelected(
              "subIBIDs",
              subIBs.map((subIB) => subIB.value),
            )
          }
        />
      )}
      <div>
        <SearchInput
          label={translatedContent.searchLabel}
          value={search}
          onChange={handleSearchChange}
          placeholder={searchTitle}
        />
      </div>
      <div className="flex h-[72px] flex-row items-end justify-end">
        <Button
          as="button"
          type="button"
          size="ps-btn-md"
          variant="ps-btn-secondary"
          onClick={handleExportButton}
        >
          {translatedContent.exportCSVText}
        </Button>
      </div>
    </div>
  );
};
